'use client';

import Link from 'next/link';
import CountryFAQ from '@/components/country/CountryFAQ';
import { t } from '@/i18n/translations';

const slugify = (s) => String(s || 'general').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'general';
const humanise = (s) => String(s || '').replace(/[-_]+/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

/**
 * eSIM / VPN / Transport FAQ subpage — "Gallery · Field" design.
 * Groups content.faq by `category` with a sticky jump nav + a related/CTA band.
 */
export default function GfServiceFaqPage({ content, lang = 'ru', country = 'montenegro', serviceCode = 'esim' }) {
    const ui = t(lang, 'serviceWidgetsUi').faqPage;
    const faq = Array.isArray(content?.faq) ? content.faq : [];
    const labels = ui.cat || {};

    // Group preserving first-appearance order.
    const order = [];
    const groups = new Map();
    faq.forEach((item) => {
        const key = item.category || 'general';
        if (!groups.has(key)) { groups.set(key, []); order.push(key); }
        groups.get(key).push(item);
    });

    const catLabel = (key) => labels[key] || humanise(key) || ui.catFallback;
    const serviceLabel = serviceCode === 'esim' ? 'eSIM' : serviceCode === 'vpn' ? 'VPN' : serviceCode === 'tours' ? ui.toursLabel : ui.transportLabel;
    const cName = content?.countryName || (country ? country.charAt(0).toUpperCase() + country.slice(1) : '');
    const backHref = `/${lang}/${country}/${serviceCode}`;
    const ctaHref = serviceCode === 'tours' ? `${backHref}#compare` : backHref;

    const related = [
        { href: backHref, label: ui.hubLabel?.[serviceCode] || ui.plansFor(serviceLabel) },
        ...(content?.airports || []).map((a) => ({ href: `/${lang}/${country}/airport/${a.iata}`, label: ui.airportLink(a.name, a.iata) })),
        serviceCode === 'esim' && { href: `/${lang}/${country}/esim/device-check`, label: ui.deviceCheck },
        serviceCode === 'esim' && { href: `/${lang}/${country}/esim/promotions`, label: ui.promoDeals },
        serviceCode === 'esim' && { href: `/${lang}/${country}/esim/quiz`, label: ui.findPlan },
    ].filter(Boolean);

    const cta = ui.ctaBy?.[serviceCode] || { title: ui.ctaTitle, text: ui.ctaText, btn: ui.ctaBtn };

    return (
        <div className="gf-root">
            {/* ── MASTHEAD ─────────────────────────────────────── */}
            <section className="fq-mast">
                <nav className="fq-back-wrap"><Link className="fq-back" href={backHref}>{ui.back(serviceLabel, cName)}</Link></nav>
                <p className="fq-kick">{`${serviceLabel}${cName ? ' · ' + cName : ''} · FAQ`}</p>
                <h1>{content?.title || ui.titleFallback(serviceLabel)}</h1>
                {content?.subtitle && <p className="sub">{content.subtitle}</p>}
            </section>

            {/* ── CATEGORY JUMP ────────────────────────────────── */}
            {order.length > 1 && (
                <nav className="fq-jump" aria-label={ui.categoriesAria}>
                    <div className="fq-jump-in">
                        {order.map((key) => <a key={key} href={`#cat-${slugify(key)}`}>{catLabel(key)}</a>)}
                    </div>
                </nav>
            )}

            {/* ── FAQ SECTIONS ─────────────────────────────────── */}
            {order.map((key, i) => (
                <section className="fq-sec" id={`cat-${slugify(key)}`} key={key}>
                    <div className="fq-cat"><span className="ix">{String(i + 1).padStart(2, '0')} · </span>{catLabel(key)}</div>
                    <CountryFAQ faqItems={groups.get(key)} lang={lang} countryName={cName} countrySlug={country} variant="gf" />
                </section>
            ))}

            {/* ── RELATED + CTA ────────────────────────────────── */}
            <div className="fq-foot-band">
                <div className="fq-rel">
                    <h3>{ui.seeAlso}</h3>
                    <ul>
                        {related.map((r) => <li key={r.href}><Link href={r.href}>{r.label}</Link></li>)}
                    </ul>
                </div>
                <div className="fq-cta2">
                    <h3>{cta.title}</h3>
                    <p>{cta.text}</p>
                    <Link href={ctaHref}>{cta.btn}</Link>
                </div>
            </div>
        </div>
    );
}
